import { formatCurrency, type MoneyContext } from '@/utils/formatCurrency';
import { keywordReportLookupKey } from '@/api/reportAdProducts';
import type { KeywordAdProduct } from '@/api/unifiedKeywords';

export type ReportRow = {
  adProduct?: string;
  campaignId?: string;
  campaignName?: string;
  keywordId?: string;
  keywordText?: string;
  matchType?: string;
  targetId?: string;
  targetingText?: string;
  adGroupId?: string;
  impressions?: number;
  clicks?: number;
  cost?: number;
  purchases30d?: number;
  sales30d?: number;
  ctr?: number;
  cpc?: number;
  roas?: number;
};

export type SpKeywordRow = {
  keyword_id?: string;
  keyword_text?: string;
  match_type?: string;
  state?: string;
  bid?: number;
  campaign_id?: string;
  ad_group_id?: string;
  extended_data?: {
    serving_status?: string;
    serving_status_details?: string[];
  } | null;
};

export type KeywordMetrics = {
  impressions: number | null;
  clicks: number | null;
  ctr: number | null;
  totalCost: number | null;
  cpc: number | null;
  purchases: number | null;
  sales: number | null;
  roas: number | null;
};

function formatMoney(amount: number, context?: MoneyContext): string {
  return formatCurrency(amount, context, {
    empty: '-',
    minimumFractionDigits: 2,
  });
}

export function parseNumberish(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

export function formatNumberValue(value: number | null, fractionDigits = 2): string {
  if (value === null) return '-';
  return new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(value);
}

export function formatPercentValue(value: number | null): string {
  if (value === null) return '-';
  return `${value.toFixed(2)}%`;
}

export function formatCurrencyValue(value: number | null, context?: MoneyContext): string {
  if (value === null) return '-';
  return formatMoney(value, context);
}

export function metricsFromReportRow(row: ReportRow | undefined): KeywordMetrics {
  if (!row) {
    return {
      impressions: null,
      clicks: null,
      ctr: null,
      totalCost: null,
      cpc: null,
      purchases: null,
      sales: null,
      roas: null,
    };
  }
  const impressions = parseNumberish(row.impressions);
  const clicks = parseNumberish(row.clicks);
  const totalCost = parseNumberish(row.cost);
  const cpc =
    clicks !== null && clicks > 0 && totalCost !== null ? totalCost / clicks : parseNumberish(row.cpc);
  return {
    impressions,
    clicks,
    ctr: parseNumberish(row.ctr),
    totalCost,
    cpc,
    purchases: parseNumberish(row.purchases30d),
    sales: parseNumberish(row.sales30d),
    roas: parseNumberish(row.roas),
  };
}

export function metricsFromKeywordSources(
  sources: Array<{ adProduct: KeywordAdProduct; keyword_id?: string | number }>,
  metricsByKeyword: Map<string, ReportRow>
): KeywordMetrics {
  let clicks = 0;
  let cost = 0;
  let purchases = 0;
  let sales = 0;
  let impressions = 0;
  let found = false;
  for (const source of sources) {
    const keywordId = source.keyword_id != null ? String(source.keyword_id).trim() : '';
    if (!keywordId) continue;
    const row = metricsByKeyword.get(keywordReportLookupKey(source.adProduct, keywordId));
    if (!row) continue;
    found = true;
    clicks += parseNumberish(row.clicks) ?? 0;
    cost += parseNumberish(row.cost) ?? 0;
    purchases += parseNumberish(row.purchases30d) ?? 0;
    sales += parseNumberish(row.sales30d) ?? 0;
    impressions += parseNumberish(row.impressions) ?? 0;
  }
  if (!found) {
    return metricsFromReportRow(undefined);
  }
  const ctr = impressions > 0 ? (clicks / impressions) * 100 : 0;
  const cpc = clicks > 0 ? cost / clicks : 0;
  const roas = cost > 0 ? sales / cost : 0;
  return metricsFromReportRow({
    impressions,
    clicks,
    cost,
    purchases30d: purchases,
    sales30d: sales,
    ctr,
    cpc,
    roas,
  });
}

export function parseReportRowsPayload(data: unknown): {
  range: { startDate?: string; endDate?: string };
  rowCount: number;
  totalCount: number;
  truncated: boolean;
  rows: ReportRow[];
} {
  if (!data || typeof data !== 'object') {
    return { range: {}, rowCount: 0, totalCount: 0, truncated: false, rows: [] };
  }
  const raw = data as Record<string, unknown>;
  const rowCount = typeof raw.rowCount === 'number' ? raw.rowCount : 0;
  const totalCount = typeof raw.totalCount === 'number' ? raw.totalCount : rowCount;
  return {
    range: (raw.range as { startDate?: string; endDate?: string }) || {},
    rowCount,
    totalCount,
    truncated: raw.truncated === true,
    rows: Array.isArray(raw.rows) ? (raw.rows as ReportRow[]) : [],
  };
}

export function parseKeywordsPayload(data: unknown): {
  keywords: SpKeywordRow[];
  totalResults: number;
  nextToken: string | null;
} {
  if (!data || typeof data !== 'object') {
    return { keywords: [], totalResults: 0, nextToken: null };
  }
  const raw = data as Record<string, unknown>;
  const keywords = Array.isArray(raw.keywords) ? (raw.keywords as SpKeywordRow[]) : [];
  const totalResults = typeof raw.total_results === 'number' ? raw.total_results : keywords.length;
  const nextToken = typeof raw.next_token === 'string' && raw.next_token.trim() ? raw.next_token : null;
  return { keywords, totalResults, nextToken };
}

export function buildKeywordMetricsMap(rows: ReportRow[]): Map<string, ReportRow> {
  const map = new Map<string, ReportRow>();
  for (const row of rows) {
    const keywordId = row.keywordId ? String(row.keywordId) : '';
    if (!keywordId) continue;
    const adProduct = row.adProduct ? String(row.adProduct) : 'sponsored_products';
    map.set(`${adProduct}::${keywordId}`, row);
  }
  return map;
}

export function keywordMetricsForCatalogRow(
  keyword: SpKeywordRow,
  adProduct: KeywordAdProduct,
  metricsByKeyword: Map<string, ReportRow>
): KeywordMetrics {
  const keywordId = keyword.keyword_id != null ? String(keyword.keyword_id).trim() : '';
  if (!keywordId) {
    return metricsFromReportRow(undefined);
  }
  const row = metricsByKeyword.get(keywordReportLookupKey(adProduct, keywordId));
  return metricsFromReportRow(row);
}

export function buildTargetMetricsMap(rows: ReportRow[]): Map<string, ReportRow> {
  const map = new Map<string, ReportRow>();
  for (const row of rows) {
    const targetId = row.targetId ? String(row.targetId) : '';
    if (!targetId) continue;
    const adProduct = row.adProduct ? String(row.adProduct) : 'sponsored_display';
    map.set(`${adProduct}::${targetId}`, row);
  }
  return map;
}

export function targetMetricsForCatalogRow(
  target: { target_id?: string | number },
  metricsByTarget: Map<string, ReportRow>
): KeywordMetrics {
  const targetId = target.target_id != null ? String(target.target_id).trim() : '';
  if (!targetId) {
    return metricsFromReportRow(undefined);
  }
  const row = metricsByTarget.get(`sponsored_display::${targetId}`);
  return metricsFromReportRow(row);
}
